import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { History, Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Money, ExportButtons } from "@/components/common";
import { api, errMsg, download } from "@/lib/api";
import { fmtDate, fmtDateTime, inr } from "@/lib/format";
import { toast } from "sonner";

function BatchDialog({ id, onOpenChange }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    if (id) api.get(`/deduction-batches/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="batch-detail-dialog">
        <DialogHeader>
          <DialogTitle>{d ? d.filename || "Bulk upload" : "Bulk upload"}</DialogTitle>
          <DialogDescription>{d && `Uploaded by ${d.uploaded_by} on ${fmtDateTime(d.uploaded_at)} · ${d.recorded} deductions · ${inr(d.amount)}`}</DialogDescription>
        </DialogHeader>
        {!d ? <Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /> : (
          <>
            <ExportButtons prefix="batch" onExport={(fmt) => download(`/deduction-batches/${id}/export`, { format: fmt })} />
            {d.net_amount !== d.amount && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">Some entries were reversed later. Amount still deducted: {inr(d.net_amount)}</div>}
            <div className="max-h-[420px] overflow-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0"><tr><th className="px-3 py-2 text-left">Employee</th><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Reference</th><th className="px-3 py-2 text-right">Deducted</th></tr></thead>
                <tbody>
                  {d.transactions.length === 0 && <tr><td colSpan={4} className="text-center text-slate-500 py-6">No deductions were recorded from this file.</td></tr>}
                  {d.transactions.map((t) => (
                    <tr key={t.id} className={`border-t ${t.reversed ? "opacity-60" : ""}`}>
                      <td className="px-3 py-2"><Link to={`/ledger/${t.employee_code}`} className="hover:text-blue-800"><span className="money text-xs text-blue-800">{t.employee_code}</span> {t.employee_name}</Link>{t.reversed && <span className="ml-2 text-[10px] uppercase border rounded px-1 text-slate-500">Reversed</span>}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(t.date)}</td>
                      <td className="px-3 py-2 text-xs">{t.reference || "—"}</td>
                      <td className="px-3 py-2 text-right text-emerald-700"><Money value={t.credit} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function UploadHistory({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/deduction-batches").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [refreshKey]);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="bulk-upload-history">
      <div className="px-5 py-4 border-b flex items-center gap-2"><History className="h-4 w-4 text-blue-800" /><h3 className="font-semibold text-slate-800">Bulk upload history</h3></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-2 text-left">Uploaded</th><th className="px-4 py-2 text-left">File</th><th className="px-4 py-2 text-left">Uploaded by</th><th className="px-4 py-2 text-right">Rows</th><th className="px-4 py-2 text-right">Recorded</th><th className="px-4 py-2 text-right">Total deducted</th><th className="px-4 py-2 text-right hidden md:table-cell">Skipped / Invalid</th><th className="px-4 py-2" /></tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={8} className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-slate-400" /></td></tr>}
            {rows?.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-500">No bulk uploads yet.</td></tr>}
            {rows?.map((b) => (
              <tr key={b.id} className="border-t hover:bg-slate-50" data-testid={`batch-row-${b.id}`}>
                <td className="px-4 py-2.5 whitespace-nowrap">{fmtDateTime(b.uploaded_at)}</td>
                <td className="px-4 py-2.5 max-w-[220px] truncate" title={b.filename}>{b.filename || "—"}</td>
                <td className="px-4 py-2.5">{b.uploaded_by}<div className="text-xs text-slate-500">{b.uploaded_by_email}</div></td>
                <td className="px-4 py-2.5 text-right money">{b.total_rows}</td>
                <td className="px-4 py-2.5 text-right money">{b.recorded}</td>
                <td className="px-4 py-2.5 text-right text-emerald-700 font-semibold" data-testid={`batch-amount-${b.id}`}><Money value={b.amount} /></td>
                <td className="px-4 py-2.5 text-right money text-slate-500 hidden md:table-cell">{b.skipped + b.duplicates} / {b.invalid}</td>
                <td className="px-4 py-2.5 text-right"><Button variant="ghost" size="sm" onClick={() => setOpen(b.id)} data-testid={`batch-view-${b.id}`}><Eye className="h-4 w-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <BatchDialog id={open} onOpenChange={(o) => !o && setOpen(null)} />
    </div>
  );
}
